import { Trophy } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { useCrmActionOutcomeLeaderboard } from "@/hooks/use-crm-action-outcome-leaderboard";

function prettyActionLabel(value: string | null | undefined): string {
  if (!value || value === "unknown") {
    return "Unknown action";
  }

  return value
    .replace(/^crm\./, "")
    .replace(/@v\d+$/, "")
    .replace(/[._-]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

interface ActionOutcomeLeaderboardProps {
  contractRef?: string;
  signalFamily?: string;
  sourceEventType?: string;
}

export function ActionOutcomeLeaderboard({
  contractRef,
  signalFamily,
  sourceEventType,
}: ActionOutcomeLeaderboardProps = {}) {
  const leaderboardFilters = {
    ...(contractRef ? { actionRef: contractRef } : {}),
    ...(signalFamily ? { signalFamily } : {}),
    ...(sourceEventType ? { sourceEventType } : {}),
  };
  const { data, isLoading, error, refetch } = useCrmActionOutcomeLeaderboard(leaderboardFilters);

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
        <div className="mb-4 flex items-center gap-2">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-4 w-52" />
        </div>
        <SkeletonCard className="h-20" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 p-4 text-sm text-[var(--color-warning)]">
        Could not load action outcome leaderboard.
        <button type="button" onClick={refetch} className="ml-2 underline">Retry</button>
      </div>
    );
  }

  const items = data?.items.slice(0, 5) ?? [];

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-[var(--color-warning)]" />
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Action Outcome Leaderboard</h2>
          </div>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Best-performing CRM action contracts by accepted and materialized outcomes.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
          {data?.windowDays ?? 90}d
        </span>
      </div>

      {contractRef && (
        <p className="mb-3 rounded-md border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-3 py-2 text-xs text-[var(--color-primary)]">
          Leaderboard scoped to {prettyActionLabel(contractRef)}
        </p>
      )}
      {(signalFamily || sourceEventType) && (
        <p className="mb-3 rounded-md border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-3 py-2 text-xs text-[var(--color-primary)]">
          Signal scope {[
            signalFamily ? `${prettyActionLabel(signalFamily)} family` : null,
            sourceEventType ? prettyActionLabel(sourceEventType) : null,
          ].filter(Boolean).join(" · ")}
        </p>
      )}

      {items.length === 0 ? (
        <p className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-sm text-[var(--color-muted-foreground)]">
          No outcome history yet for this scope. Accepted and materialized recommendations will appear here.
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const accepted = item.accepted ?? 0;
            const materialized = item.materialized ?? 0;
            const reviewed = item.reviewed ?? 0;
            const crmMaterialized = item.crmMaterialized ?? 0;
            const manualResolved = item.manualResolved ?? 0;
            const hasOutcomeSplit = crmMaterialized > 0 || manualResolved > 0;

            return (
              <div
                key={`${item.actionRef}:${item.intent}`}
                className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-xs"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-[var(--color-foreground)]">{prettyActionLabel(item.actionRef)}</p>
                    <p className="text-[var(--color-muted-foreground)]">{prettyActionLabel(item.intent)}</p>
                  </div>
                  <span className="rounded-full border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-2 py-0.5 text-[var(--color-primary)]">
                    {percent(item.materializationRate ?? 0)} materialized
                  </span>
                </div>
                <p className="mt-2 text-[var(--color-muted-foreground)]">
                  {materialized}/{accepted} accepted materialized
                  {hasOutcomeSplit ? ` (${crmMaterialized} CRM · ${manualResolved} manual)` : ""}, {reviewed} reviewed,
                  evidence {item.evidenceAgeDays == null ? "freshness unknown" : `${item.evidenceAgeDays}d old`}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
